"use client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import StarRating from "@/custom/StarRating";
import { Filter } from "lucide-react";

const Filters = ({ authors, categories, publishers }) => {
  const router = useRouter();
  let queryParams;

  const reset = (name) => {
    if (typeof window !== "undefined") {
      queryParams = new URLSearchParams(window.location.search);
    }

    if (queryParams.has(name)) {
      queryParams.delete(name);
      const newUrl = window.location.pathname + "?" + queryParams.toString();
      router.push(newUrl);

      const checkboxes = document.getElementsByName(name);
      checkboxes.forEach((item) => {
        if (item !== name) item.checked = false;
      });
    }
  };

  function handleClick(checkbox) {
    if (typeof window !== "undefined") {
      queryParams = new URLSearchParams(window.location.search);
    }
    const checkboxes = document.getElementsByName(checkbox.name);

    checkboxes.forEach((item) => {
      if (item !== checkbox) item.checked = false;
    });
    if (checkbox.checked === false) {
      queryParams.delete(checkbox.name);
    } else {
      if (queryParams.has(checkbox.name)) {
        queryParams.set(checkbox.name, checkbox.value);
      } else {
        queryParams.append(checkbox.name, checkbox.value);
      }
    }
    const path = window.location.pathname + "?" + queryParams.toString();
    router.push(path);
  }

  function checkHandler(checkBoxType, checkBoxValue) {
    if (typeof window !== "undefined") {
      queryParams = new URLSearchParams(window.location.search);

      const value = queryParams.get(checkBoxType);
      if (checkBoxValue === value) return true;
      return false;
    }
  }

  return (
    <>
      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger className="border border-brand-light bg-white flex items-center gap-2 p-2 px-3 text-brand-navy font-medium hover:border-brand-sky hover:text-brand-sky transition-colors shadow-brand-sm">
            Filters: <Filter size={20} />
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle className="text-base text-brand-navy">
                REFINE YOUR RESULTS
              </SheetTitle>
            </SheetHeader>
            <Accordion type="multiple" collapsible className="w-full">
              <AccordionItem value="Sort">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Sort By
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                    <ul className="space-y-4">
                      <li>
                        <label className="flex items-center">
                          <input
                            name="sort"
                            type="checkbox"
                            value="price"
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("sort", "price")}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            Price Low to High{" "}
                          </span>
                        </label>
                      </li>
                      <li>
                        <label className="flex items-center">
                          <input
                            name="sort"
                            type="checkbox"
                            value="-price"
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("sort", "-price")}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            Price High to Low{" "}
                          </span>
                        </label>
                      </li>
                      <li>
                        <label className="flex items-center">
                          <input
                            name="sort"
                            type="checkbox"
                            value="-createdAt"
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("sort", "-createdAt")}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            Recently Published{" "}
                          </span>
                        </label>
                      </li>
                      <li>
                        <label className="flex items-center">
                          <input
                            name="sort"
                            type="checkbox"
                            value="-rating"
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("sort", "-rating")}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            Top Rating{" "}
                          </span>
                        </label>
                      </li>
                      <li>
                        <label className="flex items-center">
                          <input
                            name="sort"
                            type="checkbox"
                            value="rating"
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("sort", "rating")}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            Low Rating{" "}
                          </span>
                        </label>
                      </li>
                    </ul>
                    <div className="m-4">
                      <Button
                        variant="outline"
                        className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                        onClick={() => reset("sort")}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="Authors">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Authors
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                    <ul className="space-y-4">
                      {authors?.map((x, i) => (
                        <li key={i}>
                          <label className="flex items-center">
                            <input
                              name="author"
                              type="checkbox"
                              value={x}
                              className="h-4 w-4 accent-brand-sky"
                              defaultChecked={checkHandler("author", x)}
                              onClick={(e) => handleClick(e.target)}
                            />
                            <span className="ml-2 cursor-pointer text-foreground capitalize">
                              {" "}
                              {x}{" "}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <div className="m-4">
                      <Button
                        variant="outline"
                        className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                        onClick={() => reset("author")}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="Categories">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Categories
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                    <ul className="space-y-4">
                      {categories?.map((x, i) => (
                        <li key={i}>
                          <label className="flex items-center">
                            <input
                              name="category"
                              type="checkbox"
                              value={x}
                              className="h-4 w-4 accent-brand-sky"
                              defaultChecked={checkHandler("category", x)}
                              onClick={(e) => handleClick(e.target)}
                            />
                            <span className="ml-2 cursor-pointer text-foreground capitalize">
                              {" "}
                              {x}{" "}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <div className="m-4">
                      <Button
                        variant="outline"
                        className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                        onClick={() => reset("category")}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="Publishers">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Publishers
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                    <ul className="space-y-4">
                      {publishers?.map((x, i) => (
                        <li key={i}>
                          <label className="flex items-center">
                            <input
                              name="publisher"
                              type="checkbox"
                              value={x}
                              className="h-4 w-4 accent-brand-sky"
                              defaultChecked={checkHandler("publisher", x)}
                              onClick={(e) => handleClick(e.target)}
                            />
                            <span className="ml-2 cursor-pointer text-foreground capitalize">
                              {" "}
                              {x}{" "}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <div className="m-4">
                      <Button
                        variant="outline"
                        className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                        onClick={() => reset("publisher")}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="Languages">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Languages
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    <li>
                      <label className="flex items-center">
                        <input
                          name="language"
                          type="checkbox"
                          value="English"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("language", "English")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          English{" "}
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-brand-sky"
                          name="language"
                          value="Bangla"
                          defaultChecked={checkHandler("language", "Bangla")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          Bangla
                        </span>
                      </label>
                    </li>
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("language")}
                    >
                      Reset
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="Price">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Prices
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    <li>
                      <label className="flex items-center">
                        <input
                          name="price"
                          type="checkbox"
                          value="0-100"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("price", "0-100")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          Under BDT 100
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="price"
                          type="checkbox"
                          value="100-300"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("price", "100-300")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          BDT 100-300
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="price"
                          type="checkbox"
                          value="300-500"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("price", "300-500")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          BDT 300-500
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="price"
                          type="checkbox"
                          value="500-1000"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("price", "500-1000")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          BDT 500-1000
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-brand-sky"
                          name="price"
                          value="1000-10000"
                          defaultChecked={checkHandler("price", "1000-10000")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          Above BDT 1000
                        </span>
                      </label>
                    </li>
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("price")}
                    >
                      Reset
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="rating">
                <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                  Ratings
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    <li>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          name="rating"
                          type="checkbox"
                          value="5"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("rating", "5")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <StarRating rate={5} size={18} />
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          name="rating"
                          type="checkbox"
                          value="4"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("rating", "4")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <StarRating rate={4} size={18} />
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          name="rating"
                          type="checkbox"
                          value="3"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("rating", "3")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <StarRating rate={3} size={18} />
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          name="rating"
                          type="checkbox"
                          value="2"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("rating", "2")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <StarRating rate={2} size={18} />
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-brand-sky"
                          name="rating"
                          value="1"
                          defaultChecked={checkHandler("rating", "1")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <StarRating rate={1} size={18} />
                      </label>
                    </li>
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("rating")}
                    >
                      Reset
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </SheetContent>
        </Sheet>
      </div>

      <Card className="hidden lg:flex w-56 min-w-[224px] flex-col border-brand-light shadow-brand-sm overflow-hidden">
        <CardHeader className="bg-brand-light/50">
          <CardTitle className="text-base text-brand-navy">
            REFINE YOUR RESULTS
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 px-4">
          <Accordion type="multiple" collapsible className="w-full">
            <AccordionItem value="Sort">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Sort By
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                  <ul className="space-y-4">
                    <li>
                      <label className="flex items-center">
                        <input
                          name="sort"
                          type="checkbox"
                          value="price"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("sort", "price")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          {" "}
                          Price Low to High{" "}
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="sort"
                          type="checkbox"
                          value="-price"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("sort", "-price")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          {" "}
                          Price High to Low{" "}
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="sort"
                          type="checkbox"
                          value="-createdAt"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("sort", "-createdAt")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          {" "}
                          Recently Published{" "}
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="sort"
                          type="checkbox"
                          value="-rating"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("sort", "-rating")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          {" "}
                          Top Rating{" "}
                        </span>
                      </label>
                    </li>
                    <li>
                      <label className="flex items-center">
                        <input
                          name="sort"
                          type="checkbox"
                          value="rating"
                          className="h-4 w-4 accent-brand-sky"
                          defaultChecked={checkHandler("sort", "rating")}
                          onClick={(e) => handleClick(e.target)}
                        />
                        <span className="ml-2 cursor-pointer text-foreground capitalize">
                          {" "}
                          Low Rating{" "}
                        </span>
                      </label>
                    </li>
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("sort")}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="Authors">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Authors
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                  <ul className="space-y-4">
                    {authors?.map((x, i) => (
                      <li key={i}>
                        <label className="flex items-center">
                          <input
                            name="author"
                            type="checkbox"
                            value={x}
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("author", x)}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            {x}{" "}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("author")}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="Categories">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Categories
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                  <ul className="space-y-4">
                    {categories?.map((x, i) => (
                      <li key={i}>
                        <label className="flex items-center">
                          <input
                            name="category"
                            type="checkbox"
                            value={x}
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("category", x)}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            {x}{" "}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("category")}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="Publishers">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Publishers
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-64 scrollbar-thin scrollbar-thumb-brand-light scrollbar-track-transparent overflow-y-auto text-foreground text-sm">
                  <ul className="space-y-4">
                    {publishers?.map((x, i) => (
                      <li key={i}>
                        <label className="flex items-center">
                          <input
                            name="publisher"
                            type="checkbox"
                            value={x}
                            className="h-4 w-4 accent-brand-sky"
                            defaultChecked={checkHandler("publisher", x)}
                            onClick={(e) => handleClick(e.target)}
                          />
                          <span className="ml-2 cursor-pointer text-foreground capitalize">
                            {" "}
                            {x}{" "}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="m-4">
                    <Button
                      variant="outline"
                      className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                      onClick={() => reset("publisher")}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="Languages">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Languages
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2">
                  <li>
                    <label className="flex items-center">
                      <input
                        name="language"
                        type="checkbox"
                        value="English"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("language", "English")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        English{" "}
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-sky"
                        name="language"
                        value="Bangla"
                        defaultChecked={checkHandler("language", "Bangla")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        Bangla
                      </span>
                    </label>
                  </li>
                </ul>
                <div className="m-4">
                  <Button
                    variant="outline"
                    className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                    onClick={() => reset("language")}
                  >
                    Reset
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="Price">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Prices
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2">
                  <li>
                    <label className="flex items-center">
                      <input
                        name="price"
                        type="checkbox"
                        value="0-100"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("price", "0-100")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        Under BDT 100
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center">
                      <input
                        name="price"
                        type="checkbox"
                        value="100-300"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("price", "100-300")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        BDT 100-300
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center">
                      <input
                        name="price"
                        type="checkbox"
                        value="300-500"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("price", "300-500")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        BDT 300-500
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center">
                      <input
                        name="price"
                        type="checkbox"
                        value="500-1000"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("price", "500-1000")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        BDT 500-1000
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-sky"
                        name="price"
                        value="1000-10000"
                        defaultChecked={checkHandler("price", "1000-10000")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <span className="ml-2 cursor-pointer text-foreground capitalize">
                        Above BDT 1000
                      </span>
                    </label>
                  </li>
                </ul>
                <div className="m-4">
                  <Button
                    variant="outline"
                    className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                    onClick={() => reset("price")}
                  >
                    Reset
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="rating">
              <AccordionTrigger className="font-medium text-sm uppercase text-brand-navy hover:text-brand-deep hover:no-underline">
                Ratings
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2">
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        name="rating"
                        type="checkbox"
                        value="5"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("rating", "5")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <StarRating rate={5} size={18} />
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        name="rating"
                        type="checkbox"
                        value="4"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("rating", "4")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <StarRating rate={4} size={18} />
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        name="rating"
                        type="checkbox"
                        value="3"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("rating", "3")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <StarRating rate={3} size={18} />
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        name="rating"
                        type="checkbox"
                        value="2"
                        className="h-4 w-4 accent-brand-sky"
                        defaultChecked={checkHandler("rating", "2")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <StarRating rate={2} size={18} />
                    </label>
                  </li>
                  <li>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-sky"
                        name="rating"
                        value="1"
                        defaultChecked={checkHandler("rating", "1")}
                        onClick={(e) => handleClick(e.target)}
                      />
                      <StarRating rate={1} size={18} />
                    </label>
                  </li>
                </ul>
                <div className="m-4">
                  <Button
                    variant="outline"
                    className="w-full border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                    onClick={() => reset("rating")}
                  >
                    Reset
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </>
  );
};

export default Filters;